import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { fetchEvents, selectAllEvents, selectEventsStatus } from '../data/eventSlice';
import { useAuth } from '../utils/AuthContext';
import { baseURL } from '../services/APIService';
import { useBackendTranslation } from '../utils/useBackendTranslation';

const Events = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const eventsData = useSelector(selectAllEvents);
  const status = useSelector(selectEventsStatus);
  const [filter, setFilter] = useState('all'); // Filter can be 'all', 'upcoming', or 'past'
  const { isAuthenticated } = useAuth();
  
  // Use the custom hook to translate events data
  const { translatedData: translatedEventsData } = useBackendTranslation(
    eventsData, 
    { skipFields: ['id', 'date', 'time', 'imageData', 'hasImage', 'created_at', 'updated_at'] }
  );
  
  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchEvents());
    }
  }, [status, dispatch]);

  // Process API events data to add upcoming status and default tags
  const events = translatedEventsData.map(event => {
    // Check if the event is upcoming based on the date
    const isUpcoming = new Date(event.date) >= new Date();
    
    // Generate tags based on location or other properties
    const tags = [];
    if (event.location) tags.push(event.location.toLowerCase());
    if (isUpcoming) tags.push('upcoming');
    
    // Determine category (you can enhance this logic as needed)
    let category = 'general';
    
    // Try to determine category from description if available
    if (event.description) {
      if (event.description.toLowerCase().includes('health')) category = 'health';
      else if (event.description.toLowerCase().includes('training') || 
               event.description.toLowerCase().includes('skill')) category = 'education';
      else if (event.description.toLowerCase().includes('legal') || 
               event.description.toLowerCase().includes('rights')) category = 'awareness';
      else if (event.description.toLowerCase().includes('fundrais')) category = 'fundraiser';
    }
    
    return {
      ...event,
      upcoming: isUpcoming,
      tags: tags,
      category: category,
      // Default time if not provided
      time: event.time || '10:00 AM'
    };
  });

  // Filter events based on selection
  const filteredEvents = events.filter(event => {
    if (filter === 'all') return true;
    return filter === 'upcoming' ? event.upcoming : !event.upcoming;
  });

  // Format date to local format
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-black text-white py-20 lg:py-32 relative overflow-hidden">
        {/* Geometric Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0"
            style={{
              backgroundImage: `url("/assets/cover.jpeg")`,
              backgroundRepeat: 'repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          ></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black mb-6 leading-tight">
              {t('events.title', 'Events & Programs')}
            </h1>
            <div className="w-24 h-1 bg-white mb-8" />
            <p className="text-xl sm:text-2xl font-light text-gray-300 leading-relaxed max-w-3xl">
              {t('events.lead', 'Join us for awareness camps, training workshops, health drives, and community gatherings.')}
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between">
            <div className="flex justify-center space-x-4 mb-4 sm:mb-0">
              <button 
                onClick={() => setFilter('all')}
                className={`px-6 py-2 rounded-full transition-all duration-300 ${filter === 'all' ? 'bg-black text-white' : 'bg-white text-black border border-gray-300 hover:bg-gray-100'}`}
              >
                {t('events.all', 'All Events')}
              </button>
              <button 
                onClick={() => setFilter('upcoming')}
                className={`px-6 py-2 rounded-full transition-all duration-300 ${filter === 'upcoming' ? 'bg-black text-white' : 'bg-white text-black border border-gray-300 hover:bg-gray-100'}`}
              >
                {t('events.upcoming', 'Upcoming')}
              </button>
              <button 
                onClick={() => setFilter('past')}
                className={`px-6 py-2 rounded-full transition-all duration-300 ${filter === 'past' ? 'bg-black text-white' : 'bg-white text-black border border-gray-300 hover:bg-gray-100'}`}
              >
                {t('events.past', 'Past Events')}
              </button>
            </div>
            
            {isAuthenticated && (
              <Link 
                to="/my-registrations" 
                className="px-6 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                My Registrations
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Events Grid Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          {filteredEvents.length === 0 ? (
            <div className="text-center py-20">
              <h3 className="text-2xl text-gray-500">
                {t('events.no_events', 'No events found')}
              </h3>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredEvents.map(event => (
                <div key={event.id} className="group relative bg-white border-2 border-gray-200 hover:border-black transition-all duration-300">
                  {/* Event image */}
                  <div className="relative overflow-hidden h-48 lg:h-64">
                    <img 
                      src={event.imageData ? event.imageData : (event.hasImage ? `${baseURL}/events/image/${event.id}` : '/assets/image1.jpeg')}
                      alt={event.title} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {/* Status badge */}
                    {event.upcoming && (
                      <div className="absolute top-4 right-4 bg-black text-white text-xs font-bold uppercase tracking-wider px-3 py-1">
                        {t('events.upcoming_badge', 'Upcoming')}
                      </div>
                    )}
                    {/* Date & Time badge - styled like a peeling corner sticker */}
                    <div className="absolute top-0 left-0 w-20 h-20">
                      <div className="absolute top-0 left-0 w-full h-full bg-white transform origin-top-left rotate-0 shadow-lg rounded-br-lg group-hover:bg-black group-hover:text-white transition-all duration-300">
                        <div className="w-full h-full flex flex-col items-center justify-center text-center pt-1">
                          <span className="text-xl font-bold text-indigo-700 group-hover:text-white transition-colors duration-300">
                            {new Date(event.date).getDate()}
                          </span>
                          <span className="text-xs uppercase font-bold tracking-wide text-indigo-600 group-hover:text-white transition-colors duration-300">
                            {new Date(event.date).toLocaleString('default', { month: 'short' })}
                          </span>
                          <span className="text-xs text-gray-600 group-hover:text-gray-300 mt-1 transition-colors duration-300">
                            {event.time || (new Date(event.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}))}
                          </span>
                        </div>
                      </div>
                      {/* Peeling effect - small triangle */}
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-gray-300 transform rotate-45 translate-y-1 translate-x-1"></div>
                    </div>
                    {/* Category tag */}
                    <div className="absolute bottom-4 left-4">
                      <span className="inline-block bg-black bg-opacity-70 text-white text-xs uppercase tracking-wider px-3 py-1 rounded-full">
                        {event.category}
                      </span>
                    </div>
                  </div>
                  
                  {/* Event details */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2 group-hover:text-black">
                      {event.title}
                    </h3>
                    <div className="flex items-center text-gray-600 text-sm mb-3">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>{formatDate(event.date)} | {event.time}</span>
                    </div>
                    <div className="flex items-center text-gray-600 text-sm mb-4">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>{event.location}</span>
                    </div>
                    <p className="text-gray-600 mb-6 line-clamp-3">{event.description}</p>
                    
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {event.tags.map((tag, index) => (
                        <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          #{tag}
                        </span>
                      ))}
                    </div>
                    
                    {/* Action button */}
                    <Link 
                      to={`/events/${event.id}`} 
                      className="inline-block border-2 border-black px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors duration-300"
                    >
                      {event.upcoming ? 
                        t('events.register', 'Register Now') : 
                        t('events.view_details', 'View Details')}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* Newsletter Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-3xl font-bold mb-4">
              {t('events.newsletter_title', 'Stay Updated on Our Events')}
            </h3>
            <p className="text-gray-300 mb-8">
              {t('events.newsletter_desc', 'Subscribe to our newsletter to receive invitations to upcoming events and programs.')}
            </p>
            <form className="flex flex-col sm:flex-row gap-4 justify-center">
              <input 
                type="email" 
                placeholder={t('events.email_placeholder', 'Your email address')}
                className="px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-white"
                required
              />
              <button 
                type="submit" 
                className="px-6 py-3 bg-white text-black font-bold rounded-md hover:bg-gray-200 transition-colors duration-300"
              >
                {t('events.subscribe', 'Subscribe')}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;
